import { Box, IconButton, Typography, useMediaQuery } from '@mui/material';
import styles from './Lineup.module.css';
import { memo, useMemo, useEffect, useState, useRef } from 'react';
import CustomImageComponent from '@/components/shared/custom/custom-image';
import Porcentajes from '../complementos/Porcentajes';
import LineupPlayerAlternatives from './LineupPlayerAlternatives';
import TshirtIconComponent from '@/components/page-components/fixtures/fixture-player-stadium-player/tshirt-icon-component';
import JugadorEventCard from '../Jugadores/JugadorEventCard';
import PuntuacionConfirmada from '../complementos/PuntuacionConfirmada';
import useFormationName from '../hooks/useFormationName';
import { getRightForPlayer } from '@/helpers/analisis-plantilla-fantasy-helper';
import useStatusIcon from '@/DesignSystem/hooks/useStatusIcon';
import useIconoEstadoDelTiempo from '../hooks/useIconoEstadoDelTiempo';
import LineupFantasySelector from './LineupFantasySelector';
import useFantasySelected from '@/DesignSystem/hooks/useFantasySelected';
import { FANTASY } from '@/helpers/biwenger-helper';
import JugadorAlineacionMercadoFantasy from '../complementos/JugadorAlineacionMercadoFantasy';
import { toPriceInEuros } from '@/helpers/prices-helper';
import LineupFantasySelectorConfirmed from './LineupFantasySelectorConfirmed';
import useShowPlayerAlineacionProbable from '../hooks/useShowPlayerAlineacionProbable';
import CountryConvocado from '../complementos/CountryConvocado';
import Apercibidos from '../complementos/Apercibidos';
import ZoomControls from '../complementos/ZoomControls';
import TeamStatsFloatingMenu from './TeamStatsFloatingMenu';
import RecomendacionesIcon from '@/components/recomendaciones/RecomendacionesIcon';
import { useAlineacionesContext } from '../hooks/useAlineacionesContext';
import LottieIconComponent, { LOTTIE_ICONS } from '@/DesignSystem/features/lottie/LottieIconComponent';

const LineupHorizontalPlayerCard = memo(
    ({
        player,
        width,
        players,
        mode,
        events,
        estadisticasPartido,
        tipo,
        top,
        left,
        right,
        fantasy,
        cardSize,
        showPercentage = true,
        ...props
    }) => {
        const handleShowPlayerAlineacionProbable = useShowPlayerAlineacionProbable(player);

        const statusIcon = useStatusIcon({
            status: player?.status,
            statusLevel: player?.statusLevel,
            width: 14,
            height: 14,
            skipIfNotOk: true,
        });

        const isSmallScreen = useMemo(() => {
            return width < 678;
        }, [width]);

        const fontSize = useMemo(() => {
            if (width < 768) {
                return 10;
            }
            return 12;
        }, [width]);

        const positionSubstEvent = useMemo(() => {
            if (width < 768) {
                return {
                    top: 0,
                    right: -10,
                };
            }
            return {
                top: 5,
                right: -5,
            };
        }, [width]);

        const playerEvents = useMemo(() => {
            return events?.filter(
                (event) =>
                    (event.player.id === player.id && event.player.id) ||
                    (event.assist.id === player.id && event.assist.id)
            );
        }, [events, player.id]);

        const estadisticaPartido = estadisticasPartido?.estadisticas?.[player?.id];

        const getPredictData = () => {
            if (mode !== 'predict') return null;

            if (
                fantasy === FANTASY.FANTASY ||
                fantasy === FANTASY.FANTASY_MARCA ||
                fantasy === FANTASY.BIWENGER ||
                fantasy === FANTASY.MISTER_FANTASY
            ) {
                let subida = player.laLigaFantasySubida;
                let marketValue = player.laLigaFantasyMarketValue;

                if (fantasy === FANTASY.FANTASY_MARCA) {
                    subida = player.fantasyMarcaSubida;
                    marketValue = player.fantasyMarcaMarketValue;
                } else if (fantasy === FANTASY.MISTER_FANTASY) {
                    subida = player.misterFantasySubida;
                    marketValue = player.misterFantasyMarketValue;
                } else if (fantasy === FANTASY.BIWENGER) {
                    subida = player.biwengerSubida;
                    marketValue = player.biwengerMarketValue;
                }

                return (
                    <>
                        <JugadorAlineacionMercadoFantasy
                            value={subida}
                            width="100%"
                            height="15px"
                            margin={'5px auto 0 auto'}
                            fantasy={fantasy}
                        />
                        <Typography
                            fontSize={10}
                            color={'#ccc'}
                            margin={'2px auto 0 auto'}
                            width={'100%'}
                            textAlign={'center'}
                            noWrap
                        >
                            {toPriceInEuros(marketValue)}
                        </Typography>
                    </>
                );
            }
        };

        return (
            <Box
                className={styles.estadioLinePlayer}
                sx={{ cursor: 'pointer', width: cardSize, left: left, right, right }}
                onClick={handleShowPlayerAlineacionProbable}
                {...props}
            >
                <Box position={'relative'}>
                    {player.image && (
                        <Box
                            position={'relative'}
                            width={cardSize}
                            height={cardSize}
                            sx={{ margin: '0 auto' }}
                        >
                            <CustomImageComponent
                                src={player.image}
                                alt={player.name}
                                width={cardSize}
                                height={cardSize}
                                style={{ borderRadius: !isSmallScreen ? '0%' : '50%' }}
                            />
                            {playerEvents?.length > 0 && (
                                <Box
                                    position={'absolute'}
                                    top={-10}
                                    left={0}
                                    width={'100%'}
                                    display={'flex'}
                                    alignItems={'center'}
                                >
                                    {playerEvents.map((event) => (
                                        <JugadorEventCard
                                            key={event.id}
                                            event={event}
                                            player={player}
                                            direction={'row'}
                                            includeTime={true}
                                        />
                                    ))}
                                </Box>
                            )}
                            <Box sx={{ position: 'absolute', bottom: -2, left: 0 }}>{statusIcon}</Box>
                            <CountryConvocado
                                country={player.countryConvocado}
                                isConvocado={player.isConvocado}
                                position={'absolute'}
                                top={5}
                                left={5}
                            />

                            {player.isBufon && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: '5px',
                                        right: '25px',
                                    }}
                                >
                                    <LottieIconComponent icon={LOTTIE_ICONS.CLOWN} width={30} height={30} />
                                </Box>
                            )}

                            <Apercibidos
                                cards={player.cards}
                                size={12}
                                color={'#fddf00'}
                                position={'absolute'}
                                bottom={0}
                                left={'0px'}
                            />

                            {mode === 'predict' && showPercentage && (
                                <Porcentajes
                                    porcentaje={player.chance}
                                    fontSize={8}
                                    display={'flex'}
                                    justifyContent={'center'}
                                    alignItems={'center'}
                                    comentario={player.comentario}
                                    width={'30px'}
                                    height={'15px'}
                                    position={'absolute'}
                                    bottom={0}
                                    right={0}
                                />
                            )}
                        </Box>
                    )}

                    {!player.image && (
                        <TshirtIconComponent
                            primary={player.primary}
                            colorNumber={player.colorNumber}
                            number={player.number}
                            width={cardSize * 0.8}
                            height={cardSize * 0.8}
                        />
                    )}

                    {getPredictData()}

                    {(mode === 'confirmed' || mode === 'last') && (
                        <PuntuacionConfirmada
                            estadisticaPartido={estadisticaPartido}
                            width="30px"
                            margin="5px auto 0 auto"
                            height="15px"
                            position="absolute"
                            bottom={0}
                            right={0}
                        />
                    )}
                </Box>
                <Typography
                    fontSize={fontSize}
                    color="#fff"
                    sx={{
                        position: 'relative',
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {player.name}
                </Typography>
                <LineupPlayerAlternatives
                    player={player}
                    players={players}
                    fontSize={fontSize}
                    width={cardSize}
                />
            </Box>
        );
    }
);

LineupHorizontalPlayerCard.displayName = 'LineupHorizontalPlayerCard';

const LineupHorizontal = ({
    home = [],
    away = [],
    prediccionTiempo,
    mode,
    events,
    estadisticasPartido,
    tipo,
    homeFormation,
    awayFormation,
    isMobile,
    homeId,
    awayId,
    fantasyEnabled,
}) => {
    const [width, setWidth] = useState(0);
    const [cardSize, setCardSize] = useState(isMobile ? 40 : 70);
    const boxRef = useRef(null);
    const isPartido = tipo === 'partido';
    const { fantasy } = useFantasySelected();

    const isPredict = mode === 'predict';

    // Obtener el editor seleccionado del contexto (funciona para Equipo y Partido)
    const { selectedEditorLineup } = useAlineacionesContext() || {};

    // Función para fusionar lineup con datos del editor
    const mergeLineupWithEditor = (lineup, teamId) => {
        if (!selectedEditorLineup || selectedEditorLineup.team !== teamId) {
            return lineup;
        }

        const editorPlayersMap = new Map(selectedEditorLineup.players?.map((p) => [p.id, p]) || []);

        return (
            lineup?.map((player) => {
                const editorPlayer = editorPlayersMap.get(player.id);

                if (editorPlayer) {
                    return {
                        ...player,
                        grid: editorPlayer.grid,
                        chance: editorPlayer.chance,
                        alternatives: editorPlayer.alternatives,
                    };
                }

                return player;
            }) || []
        );
    };

    // Fusionar home y away con datos del editor
    const finalHome = useMemo(
        () => mergeLineupWithEditor(home, homeId),
        [home, homeId, selectedEditorLineup]
    );
    const finalAway = useMemo(
        () => mergeLineupWithEditor(away, awayId),
        [away, awayId, selectedEditorLineup]
    );

    // Determinar si mostrar porcentajes según el editor seleccionado
    const showPercentage = useMemo(() => {
        if (
            selectedEditorLineup &&
            (selectedEditorLineup.team === homeId || selectedEditorLineup.team === awayId)
        ) {
            return selectedEditorLineup.usePercentage;
        }
        return true;
    }, [selectedEditorLineup, homeId, awayId]);

    const otherStyles = tipo === 'equipo' ? { justifyContent: 'space-around', width: '100%' } : {};

    if (tipo === 'equipo' && finalHome?.length === 0) {
        otherStyles.flexDirection = 'row-reverse';
    }

    const handleZoomIn = () => {
        setCardSize((prev) => Math.min(prev + 5, 90));
    };

    const handleZoomOut = () => {
        setCardSize((prev) => Math.max(prev - 5, 30));
    };

    useEffect(() => {
        const updateHeight = () => {
            if (boxRef.current) {
                const width = boxRef.current.offsetWidth;
                let height = width / 1.5;
                height = height < 400 ? 300 : height;
                setWidth(width);
            }
        };

        updateHeight();
        window.addEventListener('resize', updateHeight);
        return () => window.removeEventListener('resize', updateHeight);
    }, []);

    // Lógica dinámica basada en formación para home
    const homeFormationClean = useMemo(() => {
        // Si hay editor seleccionado para home, usar su formación
        if (selectedEditorLineup && selectedEditorLineup.team === homeId && selectedEditorLineup.formation) {
            return selectedEditorLineup.formation.replace('alineacion_', '');
        }
        if (!homeFormation) return null;
        return homeFormation.replace('alineacion_', '');
    }, [homeFormation, selectedEditorLineup, homeId]);

    const homeLines = useMemo(() => {
        if (!homeFormationClean || !finalHome?.length) return [];

        const parts = homeFormationClean.split('-').map(Number);
        const playersExcludingGoalkeeper = finalHome
            .filter((player) => player.grid !== 11 && player.grid > 0)
            .sort((a, b) => a.grid - b.grid);

        let currentIndex = 0;
        const result = parts.map((count) => {
            const group = playersExcludingGoalkeeper.slice(currentIndex, currentIndex + count);
            currentIndex += count;
            return group;
        });

        return result;
    }, [homeFormationClean, finalHome]);

    const homePortero = useMemo(() => {
        return finalHome?.find((player) => player.grid === 11);
    }, [finalHome]);

    // Lógica dinámica basada en formación para away
    const awayFormationClean = useMemo(() => {
        // Si hay editor seleccionado para away, usar su formación
        if (selectedEditorLineup && selectedEditorLineup.team === awayId && selectedEditorLineup.formation) {
            return selectedEditorLineup.formation.replace('alineacion_', '');
        }
        if (!awayFormation) return null;
        return awayFormation.replace('alineacion_', '');
    }, [awayFormation, selectedEditorLineup, awayId]);

    const awayLines = useMemo(() => {
        if (!awayFormationClean || !finalAway?.length) return [];

        const parts = awayFormationClean.split('-').map(Number);
        const playersExcludingGoalkeeper = finalAway
            .filter((player) => player.grid !== 11 && player.grid > 0)
            .sort((a, b) => a.grid - b.grid);

        let currentIndex = 0;
        const result = parts.map((count) => {
            const group = playersExcludingGoalkeeper.slice(currentIndex, currentIndex + count);
            currentIndex += count;
            return group;
        });

        return result;
    }, [awayFormationClean, finalAway]);

    const awayPortero = useMemo(() => {
        return finalAway?.find((player) => player.grid === 11);
    }, [finalAway]);

    const renderLine = (players, team, gap, teamFormation, isHome) => {
        if (!players?.length) return null;
        return (
            <Box className={styles.estadioLineHorizontal} sx={{ gap: gap }}>
                {players?.map((player, index) => {
                    let left = null;
                    let right = null;

                    if (isPartido) {
                        left = isHome ? null : getRightForPlayer(teamFormation, player.grid);
                        right = isHome ? getRightForPlayer(teamFormation, player.grid) : null;
                    } else {
                        right = getRightForPlayer(teamFormation, player.grid);
                    }

                    return (
                        <LineupHorizontalPlayerCard
                            player={player}
                            key={`${player.id}-${index}`}
                            players={team}
                            width={width}
                            mode={mode}
                            events={events}
                            estadisticasPartido={estadisticasPartido}
                            tipo={tipo}
                            right={right}
                            left={left}
                            fantasy={fantasy}
                            cardSize={cardSize}
                            showPercentage={showPercentage}
                        />
                    );
                })}
            </Box>
        );
    };

    const getGap = (linea, cantidad) => {
        let percent = 0;
        if (linea === 0) {
            switch (cantidad) {
                case 3:
                    percent = isMobile ? 4 : 8;
                    break;
                case 4:
                    percent = isMobile ? 0 : 2;
                    break;
                case 5:
                    percent = isMobile ? 0 : 0;
                    break;
                case 6:
                    percent = isMobile ? 0 : 2;
                    break;
                default:
                    percent = 0;
                    break;
            }
        } else if (linea === 1) {
            switch (cantidad) {
                case 2:
                    percent = 4;
                    break;
                case 3:
                    percent = isMobile ? 4 : 8;
                    break;
                case 4:
                    percent = 2;
                    break;
                case 5:
                    percent = isMobile ? 0 : 0;
                    break;
                default:
                    percent = 0;
                    break;
            }
        } else if (linea === 2) {
            switch (cantidad) {
                case 2:
                    percent = 8;
                    break;
                case 3:
                    percent = 8;
                    break;
                case 4:
                    percent = 2;
                    break;
                default:
                    percent = 4;
                    break;
            }
        } else if (linea === 3) {
            switch (cantidad) {
                case 2:
                    percent = 4;
                    break;
                case 3:
                    percent = 12;
                    break;
                case 4:
                    percent = 2;
                    break;
                default:
                    percent = 4;
            }
        }

        if (fantasy === FANTASY.ANALITICA) {
            percent += 4;
        }

        return percent + '%';
    };

    const tiempo = useIconoEstadoDelTiempo(prediccionTiempo);

    return (
        <Box
            ref={boxRef}
            className={styles.estadioHorizontal}
            sx={{
                width: '100%',
                transition: 'height 0.2s ease',
                position: 'relative',
            }}
        >
            {homeFormation && (
                <Typography
                    color="rgba(255, 255, 255, 0.5)"
                    sx={{
                        position: 'absolute',
                        top: { xs: 12, md: 0 },
                        left: { xs: 12, md: 16 },
                        zIndex: 1000,
                        fontSize: { xs: 10, md: 12 },
                    }}
                >
                    Formación: {useFormationName(homeFormation)}
                </Typography>
            )}

            <ZoomControls
                cardSize={cardSize}
                onZoomIn={handleZoomIn}
                onZoomOut={handleZoomOut}
                minSize={30}
                maxSize={90}
            />

            {awayFormation && (
                <Typography
                    variant="h6"
                    color="rgba(255, 255, 255, 0.5)"
                    sx={{
                        position: 'absolute',
                        top: { xs: 12, md: 0 },
                        right: { xs: 12, md: 16 },
                        zIndex: 1000,
                        fontSize: { xs: 10, md: 12 },
                    }}
                >
                    Formación: {useFormationName(awayFormation)}
                </Typography>
            )}

            {finalHome?.length > 0 && (
                <Box className={styles.estadioHorizontalEquipo} sx={{ ...otherStyles }}>
                    {homePortero && renderLine([homePortero], finalHome, 0, homeFormation, true)}
                    {homeLines.map((linePlayers, idx) => {
                        return (
                            <Box key={idx} height="100%">
                                {renderLine(
                                    linePlayers?.sort((a, b) => a.grid - b.grid),
                                    finalHome,
                                    getGap(idx + 1, linePlayers.length),
                                    homeFormation,
                                    true
                                )}
                            </Box>
                        );
                    })}
                </Box>
            )}

            {finalAway?.length > 0 && (
                <Box className={styles.estadioHorizontalEquipo} sx={{ ...otherStyles }}>
                    {awayLines
                        .slice()
                        .reverse()
                        .map((linePlayers, idx) => {
                            const originalIdx = awayLines.length - 1 - idx;
                            const lineSorted = isPartido
                                ? linePlayers?.sort((a, b) => b.grid - a.grid)
                                : linePlayers?.sort((a, b) => a.grid - b.grid);
                            return (
                                <Box key={originalIdx} height="100%">
                                    {renderLine(
                                        lineSorted,
                                        finalAway,
                                        getGap(originalIdx + 1, linePlayers.length),
                                        awayFormation,
                                        false
                                    )}
                                </Box>
                            );
                        })}
                    {awayPortero && renderLine([awayPortero], finalAway, 0, awayFormation, false)}
                </Box>
            )}
            {tiempo}
            {isPredict ? (
                <Box sx={{ position: 'absolute', bottom: isMobile ? 10 : 30, left: isMobile ? 10 : 25 }}>
                    <LineupFantasySelector size={isMobile ? 18 : 24} />
                </Box>
            ) : (
                <Box sx={{ position: 'absolute', bottom: 30, left: 25 }}>
                    <LineupFantasySelectorConfirmed fantasyEnabled={fantasyEnabled} />
                </Box>
            )}
            {finalHome?.length > 0 && homeId > 0 && (
                <TeamStatsFloatingMenu teamId={homeId} top="25px" left="25px" />
            )}
            {finalAway?.length > 0 && awayId > 0 && (
                <TeamStatsFloatingMenu teamId={awayId} bottom="60px" left="25px" />
            )}

            <RecomendacionesIcon sx={{ position: 'absolute', bottom: '20px', right: '10px' }} size={15} />
        </Box>
    );
};

export default LineupHorizontal;
